import React, { useState, useEffect } from 'react';
import classnames from 'classnames/bind';
import PieChart from 'react-minimal-pie-chart';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import useMousePosition from '@react-hook/mouse-position';
import { useCountUp } from 'react-countup';

import { withSnapPoint } from '@components/snapping-slider';
import Richtext, { summariseRichText } from '@components/rich-text';
import { LeftShard, RightShard, LeftGreyShard, RightGreyShard } from '@components/shards';
import { formatNumber } from '@utils/number';
import css from './stat.module.scss';

const styles = classnames.bind(css);

const ProgressChart = ({ active, largeChart, value, unit, label }) => {
    const { countUp, update } = useCountUp({
        end: 0,
        duration: 0.8,
    });

    // If slide is active then start the animation sequence.
    useEffect(() => {
        if (active) update(value);
    });

    return (
        <div
            aria-label={`${value}${unit} ${label}`}
            className={styles('progress-chart', {
                'progress-chart--large': largeChart,
            })}
        >
            <div aria-hidden className={styles('progress-chart__chart')}>
                <CircularProgressbarWithChildren
                    value={countUp}
                    strokeWidth={15}
                    styles={buildStyles({
                        pathTransition: 'none',
                        pathColor: '#1f874a',
                        trailColor: '#c5edb9',
                        strokeLinecap: 'butt',
                    })}
                >
                    <span className={styles('progress-chart__value')}>
                        {countUp}
                        <span>{unit}</span>
                    </span>
                </CircularProgressbarWithChildren>
            </div>

            <span aria-hidden className={styles('stat-slide__label')}>
                {label}
            </span>
        </div>
    );
};

const ChartTooltip = ({ mousePosition, value, unit, color }) => {
    return (
        <div
            className={styles('tooltip')}
            style={{
                opacity: value && mousePosition ? 1 : 0,
                left: mousePosition.x - 10,
                top: mousePosition.y - 100,
                background: '#fff',
            }}
        >
            {Math.round(value)}
            <span>{unit}</span>
        </div>
    );
};

const DonutChart = ({ data, active, largeChart }) => {
    const [mousePositionRef, mousePosition] = useMousePosition(0, 0, 60);
    const [currentHighlight, setCurrentHighlight] = useState(null);
    const colors = ['#c5edb9', '#1f874a'];
    const chartData = data.map((data, index) => ({
        ...data,
        color: colors[index],
    }));

    return (
        <div ref={mousePositionRef} className={styles('donut-chart', { 'donut-chart--large': largeChart })}>
            <PieChart
                aria-hidden
                data={chartData}
                startAngle={270}
                lineWidth={active ? 30 : 0}
                style={{
                    width: largeChart ? 230 : 200,
                    margin: '0 auto',
                }}
                animate={true}
                background="#d6d6d6"
                reveal={active ? 100 : 0}
                onMouseOver={(e, data, index) => setCurrentHighlight(data[index])}
                onMouseOut={() => setCurrentHighlight(null)}
            />
            <ChartTooltip {...currentHighlight} mousePosition={mousePosition} />
            <div className={styles('donut-chart__key')}>
                {chartData.map((data, index) => {
                    return (
                        <div
                            aria-label={`${Math.round(data.value)}${data.unit} ${data.label}`}
                            key={`donut-label-${index}`}
                            className={styles('donut-chart__key__item')}
                        >
                            <span
                                aria-hidden
                                className={styles('donut-chart__key__dot')}
                                style={{ background: data.color }}
                            />
                            <span aria-hidden>
                                {Math.round(data.value)}
                                {data.unit} {data.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CountUpChart = ({ active, value, label, prefix, suffix, icon, largeChart }) => {
    const { countUp, update } = useCountUp({
        end: 0,
        duration: 1,
    });

    // If slide is active then start the animation sequence.
    useEffect(() => {
        if (active) update(value);
    });

    return (
        <div
            aria-label={`${value} ${summariseRichText(label)}`}
            className={styles('count-up', { 'count-up-chart--large': largeChart })}
        >
            {icon && (
                <div className={styles('count-up-chart__icon-container')}>
                    <img
                        alt=""
                        aria-hidden
                        src={require(`../../images/stat-slide/icons/${icon}.svg`)}
                        className={styles('count-up-chart__icon')}
                    />
                </div>
            )}
            <span className={styles('count-up-chart__value')} aria-hidden>
                {formatNumber(countUp)}
            </span>
            <Richtext aria-hidden className={styles('stat-slide__label')}>
                {label}
            </Richtext>
        </div>
    );
};

const StatSlide = ({ index, title, charts = [], active, slideRef }) => {
    const [hasAnimated, setHasAnimated] = useState(false);

    const renderChart = (chart) => {
        switch (chart.type) {
            case 'donut':
                return DonutChart;
            case 'number':
                return CountUpChart;
            case 'progress':
                return ProgressChart;
            default:
                console.error(`Incorrect stat type: ${chart.type}`);
                return () => null;
        }
    };

    const renderShards = (chart) => {
        switch (chart.type) {
            case 'donut':
                return (
                    <>
                        <LeftShard />
                        <RightShard />
                    </>
                );
            case 'number':
                return (
                    <>
                        <LeftGreyShard />
                        <RightGreyShard />
                    </>
                );
            case 'progress':
                return (
                    <>
                        <LeftShard />
                        <RightShard />
                    </>
                );
            default:
                return (
                    <>
                        <LeftShard />
                        <RightShard />
                    </>
                );
        }
    };

    if (active === true && !hasAnimated) {
        setHasAnimated(true);
    }

    return (
        <div ref={slideRef} className={styles('stat-slide')}>
            <h3 className={styles('stat-slide__title')}>{title}</h3>
            <div
                className={styles(
                    'stat-slide__charts',
                    { 'stat-slide__charts--wrap': charts.length >= 3 },
                    { 'stat-slide__charts--two': charts.length === 2 },
                )}
            >
                {charts.map((chart, chartIndex) => {
                    const Chart = renderChart(chart);
                    return (
                        <Chart
                            largeChart={charts.length === 1}
                            key={`chart-${index}-${chartIndex}`}
                            active={active}
                            {...chart}
                        />
                    );
                })}
            </div>
            {charts.map((chart, chartIndex) => {
                return <React.Fragment key={`chart-${index}-${chartIndex}`}> {renderShards(chart)}</React.Fragment>;
            })}
        </div>
    );
};

export default withSnapPoint(StatSlide);
